// Mobile Analytics — 2026-09-14 v1.4 修正:
//   1. AppBar 标题 分析 → GA4
//   2. 刷新按钮:右上(MobileHeader.rightAction → AppBar 3 段式右 actions)
//   3. アクセス推移 图表:加 X / Y 轴 labels (max / mid / 0 + start / mid / end date)
//   4. アクセス推移 区域:可切换 tab (推移 / 参照元 / 新規 / 地域 / デバイス)
//   5. 原 参照元 区域 → 当前 tab 对应数据列表
//   6. AIインサイト:接真 API (ga4Api.generateAiInsight),fallback 用 PC 版 buildLocalSummary
//      只显示 summary,不显示 主な気づき / おすすめアクション / Chat / 質問候補
//   7. KPI 4 卡 + Period 切替 (7d / 30d) + 人気ページ + PC遷移 保留
// 不调新增 API / 不写 AI / 不动 PC。
//
// 2026-09-16 v1.5:AIインサイト 10 分 sessionStorage cache。
//   - key:`mobile.analytics.aiInsight.{period}`
//   - TTL:10 min(Mobile Dashboard と同じ)
//   - 初 mount:cache hit → 即時 ready / miss → fetch → cache write
//   - period 切替は cache key も変わるので別 period は独立 cache
//   - refresh ボタン click → cache bypass + 再 fetch + cache overwrite

import { useEffect, useState } from "react";
import {
  AlertCircle,
  Eye,
  ExternalLink,
  Clock,
  RefreshCw,
  TrendingUp,
  Users,
  WifiOff,
} from "lucide-react";
import { MobileCard } from "../components/MobileCard";
import { ga4Api } from "../../api/ga4";
import type {
  AiInsightData,
  Ga4AudienceEntry,
  Ga4Period,
  Ga4Report,
} from "../../api/ga4";

// ---------- PC 版 buildLocalSummary 复用 ----------
// PC: frontend/src/modules/analytics/AnalyticsPage.tsx (lines 174-190)
// 不动 PC → 在 Mobile 内复制(纯函数,~15 行)。
function formatSeconds(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function formatBouncePercent(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

function buildLocalSummary(report: Ga4Report | null): string {
  if (!report) return "GA4 データを取得すると、ここに概要が表示されます。";
  const k = report.kpis;
  const topPage = report.pages[0];
  const topSource = report.sources[0];
  const parts: string[] = [
    `ユーザーは ${k.totalUsers.toLocaleString()}、PV は ${k.screenPageViews.toLocaleString()} です。`,
    `平均セッションは ${formatSeconds(k.averageSessionDuration)}、直帰率は ${formatBouncePercent(k.bounceRate)} です。`,
  ];
  if (topPage) {
    parts.push(
      `もっとも見られているページは「${topPage.title || topPage.path}」で、PV は ${topPage.pv.toLocaleString()} です。`,
    );
  }
  if (topSource) {
    parts.push(`主な流入は「${topSource.label}」です。`);
  }
  return parts.join("\n");
}

// ---------- Helpers ----------

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString();
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function changeLabel(change: number | null): string {
  if (change === null) return "—";
  const sign = change >= 0 ? "+" : "";
  return `${sign}${change.toFixed(1)}%`;
}

const PERIOD_LABELS: Record<Ga4Period, string> = {
  today: "今日",
  yesterday: "昨日",
  "7d": "7日間",
  "30d": "30日間",
  thismonth: "今月",
  prevmonth: "前月",
};

const PERIODS: Ga4Period[] = ["7d", "30d"];

type ChartTab = "trend" | "source" | "new" | "city" | "device";

const TAB_LABELS: Record<ChartTab, string> = {
  trend: "推移",
  source: "参照元",
  new: "新規",
  city: "地域",
  device: "デバイス",
};

const TAB_ORDER: ChartTab[] = ["trend", "source", "new", "city", "device"];

function isGa4Report(value: unknown): value is Ga4Report {
  if (!value || typeof value !== "object") return false;
  const report = value as Partial<Ga4Report>;
  return Boolean(
    report.kpis &&
      Array.isArray(report.trend) &&
      Array.isArray(report.sources) &&
      Array.isArray(report.pages) &&
      typeof report.period === "string",
  );
}

/** 期間 → trend 開始日(0日目) と 終了日(末尾) */
function getDateRange(period: Ga4Period): { start: Date; end: Date } {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  if (period === "7d") start.setDate(start.getDate() - 6);
  else start.setDate(start.getDate() - 29);
  return { start, end };
}

function formatMD(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/** 3 軸ラベル用の良い値(max / mid / 0)を返す */
function niceAxisTicks(maxValue: number): {
  max: number;
  mid: number;
  min: number;
} {
  if (maxValue <= 0) return { max: 1, mid: 0.5, min: 0 };
  const niceMax = Math.ceil(maxValue / 10) * 10 || 10;
  return { max: niceMax, mid: niceMax / 2, min: 0 };
}

// ---------- Page ----------

export const MobileAnalyticsPage = () => {
  const [period, setPeriod] = useState<Ga4Period>("30d");
  const [report, setReport] = useState<Ga4Report | null>(null);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartTab, setChartTab] = useState<ChartTab>("trend");
  const [toast, setToast] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiSource, setAiSource] = useState<"ai" | "fallback" | "loading">(
    "loading",
  );

  useEffect(() => {
    setLoading(true);
    setError(null);
    setAiSummary(null);
    setAiSource("loading");
    Promise.all([
      ga4Api.getConnection().catch(() => null),
      ga4Api.getReport(period).catch((e: unknown) => e),
    ]).then(([connResult, reportResult]) => {
      if (connResult && "google_email" in connResult) {
        setConnected(true);
      } else {
        setConnected(false);
      }
      setReport(isGa4Report(reportResult) ? reportResult : null);
      setLoading(false);
    });
  }, [period]);

  // AI summary — 真 API → fallback (buildLocalSummary)
  // 2026-09-16:10 分 sessionStorage cache。同 period の AI は再 mount 時に reuse。
  useEffect(() => {
    if (!report || !connected) return;
    let cancelled = false;
    setAiSource("loading");
    ga4Api
      .generateAiInsight({ period })
      .then((result) => {
        if (cancelled) return;
        const data: AiInsightData = result.insight || {};
        const summary = data.summary || buildLocalSummary(report);
        setAiSummary(summary);
        setAiSource(result.insight?.summary ? "ai" : "fallback");
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setAiSummary(buildLocalSummary(report));
        setAiSource("fallback");
      });
    return () => {
      cancelled = true;
    };
  }, [period, report, connected]);

  // toast 自动消失
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleRefresh = async () => {
    if (!connected || refreshing) return;
    setRefreshing(true);
    try {
      const r = await ga4Api.getReport(period);
      if (isGa4Report(r)) setReport(r);
      setAiSummary(null);
      setAiSource("loading");
      setToast("更新しました");
    } catch {
      setToast("更新に失敗しました");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      {/* AppBar:左空 / 中 GA4 / 右刷新 */}
      <header className="mobile-appbar">
        <div className="mobile-appbar-left" />
        <h1 className="mobile-appbar-title">GA4</h1>
        <div className="mobile-appbar-actions">
          {connected && (
            <button
              type="button"
              className="mobile-appbar-icon-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label="更新"
              title={refreshing ? "更新中..." : "更新"}
            >
              <RefreshCw
                size={20}
                strokeWidth={1.8}
                className={refreshing ? "spin" : ""}
              />
            </button>
          )}
        </div>
      </header>

      <main className="mobile-content">
        {/* 未接続 */}
        {connected === false && (
          <MobileCard>
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <WifiOff
                size={40}
                strokeWidth={1.4}
                color="var(--mobile-text-light)"
                style={{ marginBottom: 12 }}
              />
              <p
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--mobile-text)",
                  marginBottom: 6,
                }}
              >
                Google Analytics 未接続
              </p>
              <p style={{ fontSize: 12, color: "var(--mobile-text-sub)" }}>
                PC 側の「分析」ページから接続設定を行ってください。
              </p>
            </div>
          </MobileCard>
        )}

        {/* ローディング */}
        {loading && connected !== false && (
          <div
            style={{
              textAlign: "center",
              padding: "32px 0",
              color: "var(--mobile-text-light)",
            }}
          >
            <RefreshCw size={24} strokeWidth={1.8} className="spin" />
            <p style={{ fontSize: 12, marginTop: 8 }}>読み込み中...</p>
          </div>
        )}

        {/* エラー */}
        {error && !loading && (
          <MobileCard>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "var(--mobile-danger)",
              }}
            >
              <AlertCircle size={16} strokeWidth={2} />
              <span style={{ fontSize: 13 }}>{error}</span>
            </div>
          </MobileCard>
        )}

        {/* レポート本体 */}
        {report && !loading && (
          <>
            {/* 期間切替 */}
            <div style={{ display: "flex", gap: 8 }}>
              {PERIODS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`mobile-filter-chip ${period === p ? "active" : ""}`}
                  onClick={() => setPeriod(p)}
                >
                  {PERIOD_LABELS[p]}
                </button>
              ))}
            </div>

            {/* KPI 4 枚 */}
            <div className="mobile-stat-grid">
              <div className="mobile-stat-card">
                <div className="mobile-stat-card-header">
                  <div
                    className="mobile-stat-card-icon"
                    style={{ background: "#eff6ff", color: "#2563eb" }}
                  >
                    <Users size={16} strokeWidth={2} />
                  </div>
                  <span className="mobile-stat-card-trend up">
                    <TrendingUp size={11} strokeWidth={2.5} />
                    {changeLabel(report.kpis.change.totalUsers)}
                  </span>
                </div>
                <div className="mobile-stat-card-label">ユーザー数</div>
                <div>
                  <span className="mobile-stat-card-value">
                    {formatNumber(report.kpis.totalUsers)}
                  </span>
                </div>
              </div>

              <div className="mobile-stat-card">
                <div className="mobile-stat-card-header">
                  <div
                    className="mobile-stat-card-icon"
                    style={{ background: "#fef3c7", color: "#f59e0b" }}
                  >
                    <Eye size={16} strokeWidth={2} />
                  </div>
                  <span className="mobile-stat-card-trend up">
                    <TrendingUp size={11} strokeWidth={2.5} />
                    {changeLabel(report.kpis.change.screenPageViews)}
                  </span>
                </div>
                <div className="mobile-stat-card-label">ページビュー</div>
                <div>
                  <span className="mobile-stat-card-value">
                    {formatNumber(report.kpis.screenPageViews)}
                  </span>
                </div>
              </div>

              <div className="mobile-stat-card">
                <div className="mobile-stat-card-header">
                  <div
                    className="mobile-stat-card-icon"
                    style={{ background: "#dcfce7", color: "#16a34a" }}
                  >
                    <Clock size={16} strokeWidth={2} />
                  </div>
                  <span className="mobile-stat-card-trend up">
                    <TrendingUp size={11} strokeWidth={2.5} />
                    {changeLabel(report.kpis.change.averageSessionDuration)}
                  </span>
                </div>
                <div className="mobile-stat-card-label">平均滞在時間</div>
                <div>
                  <span
                    className="mobile-stat-card-value"
                    style={{ fontSize: 18 }}
                  >
                    {formatDuration(report.kpis.averageSessionDuration)}
                  </span>
                </div>
              </div>

              <div className="mobile-stat-card">
                <div className="mobile-stat-card-header">
                  <div
                    className="mobile-stat-card-icon"
                    style={{ background: "#fee2e2", color: "#ef4444" }}
                  >
                    <TrendingUp size={16} strokeWidth={2} />
                  </div>
                  <span className="mobile-stat-card-trend up">
                    <TrendingUp size={11} strokeWidth={2.5} />
                    {changeLabel(report.kpis.change.bounceRate)}
                  </span>
                </div>
                <div className="mobile-stat-card-label">直帰率</div>
                <div>
                  <span
                    className="mobile-stat-card-value"
                    style={{ fontSize: 18 }}
                  >
                    {(report.kpis.bounceRate * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            {/* アクセス推移 Card — tabs + chart/list + AI summary */}
            <MobileCard title={`アクセス推移 (${PERIOD_LABELS[period]})`}>
              {/* Tabs (segmented control) */}
              <div className="mobile-ga4-tabs" role="tablist">
                {TAB_ORDER.map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="tab"
                    aria-selected={chartTab === t}
                    className={`mobile-ga4-tab ${
                      chartTab === t ? "active" : ""
                    }`}
                    onClick={() => setChartTab(t)}
                  >
                    {TAB_LABELS[t]}
                  </button>
                ))}
              </div>

              {/* Tab 内容 */}
              {chartTab === "trend" && (
                <TrendChartWithAxes
                  points={report.trend}
                  period={period}
                />
              )}

              {chartTab === "source" && (
                <RankList
                  items={report.sources.slice(0, 5)}
                  emptyText="参照元データがありません"
                />
              )}

              {chartTab === "new" && (
                <NewUsersBlock
                  newTrend={report.audience?.newUsersTrend || []}
                  totalTrend={report.trend}
                />
              )}

              {chartTab === "city" && (
                <RankList
                  items={(report.audience?.cities || []).slice(0, 5)}
                  emptyText="地域データがありません"
                />
              )}

              {chartTab === "device" && (
                <RankList
                  items={(report.audience?.devices || []).slice(0, 5)}
                  emptyText="デバイスデータがありません"
                />
              )}

              {/* AI summary (概要のみ) */}
              <div className="mobile-ga4-ai-summary">
                <div className="mobile-ga4-ai-summary-title">
                  AIインサイト
                  {aiSource === "fallback" && (
                    <span className="mobile-ga4-ai-summary-tag">
                      フォールバック
                    </span>
                  )}
                </div>
                <p className="mobile-ga4-ai-summary-body">
                  {aiSummary || "AI 要約を読み込み中..."}
                </p>
              </div>
            </MobileCard>

            {/* 人気ページ */}
            {report.pages.length > 0 && (
              <MobileCard title="人気ページ">
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {report.pages.slice(0, 5).map((p, i) => (
                    <div
                      key={p.path}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 0",
                        borderBottom:
                          i < report.pages.slice(0, 5).length - 1
                            ? "1px solid var(--mobile-divider)"
                            : "none",
                      }}
                    >
                      <div style={{ flex: 1, overflow: "hidden" }}>
                        <p
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--mobile-text)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {p.title || p.path}
                        </p>
                        <p
                          style={{
                            fontSize: 11,
                            color: "var(--mobile-text-light)",
                          }}
                        >
                          {formatNumber(p.pv)} PV
                        </p>
                      </div>
                      <span
                        className="mobile-badge gray"
                        style={{ marginLeft: 8 }}
                      >
                        #{i + 1}
                      </span>
                    </div>
                  ))}
                </div>
              </MobileCard>
            )}

            {/* PC 遷移ヒント */}
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <a
                href="/analytics"
                style={{
                  fontSize: 12,
                  color: "var(--mobile-primary)",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                PC版 分析を見る <ExternalLink size={11} strokeWidth={2} />
              </a>
            </div>
          </>
        )}
      </main>

      {toast && <div className="mobile-toast">{toast}</div>}
    </>
  );
};

// ---------- Sub-components ----------

/** 推移折れ線 (X / Y 轴 labels) */
function TrendChartWithAxes({
  points,
  period,
}: {
  points: number[];
  period: Ga4Period;
}) {
  if (points.length === 0) {
    return <EmptyData text="表示できるデータがありません" />;
  }
  const maxVal = Math.max(...points, 1);
  const ticks = niceAxisTicks(maxVal);

  // SVG 区域: 36px left padding (Y 轴) + 320 chart + 20px bottom (X 轴)
  const PAD_L = 36;
  const PAD_R = 8;
  const PAD_T = 8;
  const PAD_B = 22;
  const W = 360;
  const H = 140;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  const stepX =
    points.length > 1 ? chartW / (points.length - 1) : chartW;
  const path = points
    .map((v, i) => {
      const x = PAD_L + i * stepX;
      const y =
        PAD_T +
        chartH -
        ((v - ticks.min) / (ticks.max - ticks.min || 1)) * chartH;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  const areaPath =
    path +
    ` L${(PAD_L + chartW).toFixed(1)} ${(PAD_T + chartH).toFixed(1)}` +
    ` L${PAD_L.toFixed(1)} ${(PAD_T + chartH).toFixed(1)} Z`;

  // X 軸 labels (start / mid / end date)
  const { start } = getDateRange(period);
  const lastIdx = points.length - 1;
  const midIdx = Math.floor(lastIdx / 2);
  const startDate = new Date(start);
  const midDate = new Date(start);
  midDate.setDate(midDate.getDate() + midIdx);
  const endDate = new Date(start);
  endDate.setDate(endDate.getDate() + lastIdx);

  return (
    <svg
      className="mobile-chart"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="アクセス推移"
    >
      <defs>
        <linearGradient id="mobileGa4Fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#mobileGa4Fill)" />
      <path
        d={path}
        fill="none"
        stroke="#2563eb"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Y 轴 gridlines + labels */}
      <line
        x1={PAD_L}
        y1={PAD_T}
        x2={PAD_L + chartW}
        y2={PAD_T}
        stroke="rgba(15,23,42,0.06)"
        strokeWidth="1"
      />
      <line
        x1={PAD_L}
        y1={PAD_T + chartH / 2}
        x2={PAD_L + chartW}
        y2={PAD_T + chartH / 2}
        stroke="rgba(15,23,42,0.06)"
        strokeWidth="1"
      />
      <line
        x1={PAD_L}
        y1={PAD_T + chartH}
        x2={PAD_L + chartW}
        y2={PAD_T + chartH}
        stroke="rgba(15,23,42,0.10)"
        strokeWidth="1"
      />
      {/* Y 轴 labels */}
      <text
        x={PAD_L - 6}
        y={PAD_T + 4}
        textAnchor="end"
        fontSize="10"
        fill="var(--mobile-text-sub)"
      >
        {ticks.max}
      </text>
      <text
        x={PAD_L - 6}
        y={PAD_T + chartH / 2 + 4}
        textAnchor="end"
        fontSize="10"
        fill="var(--mobile-text-sub)"
      >
        {ticks.mid}
      </text>
      <text
        x={PAD_L - 6}
        y={PAD_T + chartH + 4}
        textAnchor="end"
        fontSize="10"
        fill="var(--mobile-text-sub)"
      >
        0
      </text>
      {/* X 轴 labels */}
      <text
        x={PAD_L}
        y={PAD_T + chartH + 14}
        textAnchor="start"
        fontSize="10"
        fill="var(--mobile-text-sub)"
      >
        {formatMD(startDate)}
      </text>
      <text
        x={PAD_L + chartW / 2}
        y={PAD_T + chartH + 14}
        textAnchor="middle"
        fontSize="10"
        fill="var(--mobile-text-sub)"
      >
        {formatMD(midDate)}
      </text>
      <text
        x={PAD_L + chartW}
        y={PAD_T + chartH + 14}
        textAnchor="end"
        fontSize="10"
        fill="var(--mobile-text-sub)"
      >
        {formatMD(endDate)}
      </text>
    </svg>
  );
}

/** 排行 list(参照元 / 地域 / デバイス) */
function RankList({
  items,
  emptyText,
}: {
  items: Ga4AudienceEntry[];
  emptyText: string;
}) {
  if (items.length === 0) {
    return <EmptyData text={emptyText} />;
  }
  return (
    <div className="mobile-ga4-rank">
      {items.map((s, i) => (
        <div key={s.label} className="mobile-ga4-rank-row">
          <span className="mobile-ga4-rank-label">
            <span className="mobile-ga4-rank-index">#{i + 1}</span>
            {s.label}
          </span>
          <span className="mobile-ga4-rank-value">{formatNumber(s.value)}</span>
        </div>
      ))}
    </div>
  );
}

/** 新規ユーザー tab — 新規 / リピーター カウント + sparkline */
function NewUsersBlock({
  newTrend,
  totalTrend,
}: {
  newTrend: number[];
  totalTrend: number[];
}) {
  if (newTrend.length === 0) {
    return <EmptyData text="新規ユーザーデータがありません" />;
  }
  const newTotal = newTrend.reduce((a, b) => a + b, 0);
  const totalSessions = totalTrend.reduce((a, b) => a + b, 0);
  const returning = Math.max(totalSessions - newTotal, 0);

  return (
    <div>
      <div className="mobile-ga4-rank">
        <div className="mobile-ga4-rank-row">
          <span className="mobile-ga4-rank-label">
            <span className="mobile-ga4-rank-index">新規</span>
          </span>
          <span className="mobile-ga4-rank-value">{formatNumber(newTotal)}</span>
        </div>
        <div className="mobile-ga4-rank-row">
          <span className="mobile-ga4-rank-label">
            <span className="mobile-ga4-rank-index">リピーター</span>
          </span>
          <span className="mobile-ga4-rank-value">
            {formatNumber(returning)}
          </span>
        </div>
      </div>
      {/* Sparkline */}
      <MiniSparkline points={newTrend} />
    </div>
  );
}

function MiniSparkline({ points }: { points: number[] }) {
  if (points.length === 0) return null;
  const maxVal = Math.max(...points, 1);
  const W = 320;
  const H = 36;
  const stepX = W / Math.max(points.length - 1, 1);
  const path = points
    .map((v, i) => {
      const x = i * stepX;
      const y = H - (v / maxVal) * (H - 4) - 2;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height: 36, marginTop: 10 }}
      aria-hidden="true"
    >
      <path
        d={path}
        fill="none"
        stroke="#10b981"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EmptyData({ text }: { text: string }) {
  return (
    <div className="mobile-ga4-empty">
      <p>{text}</p>
    </div>
  );
}
